import React from 'react';
import styled from 'styled-components';
import SubLayout from '../components/layout/SubLayout';
import { promotionMenus } from '../data/promotionMenus';

const Intro = styled.p`
  font-size: 1.05rem;
  color: #555;
  line-height: 1.9;
  margin-bottom: 50px;
`;

const SectionLabel = styled.h3`
  font-size: 1.3rem;
  color: #1a2a40;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
`;

/*
 * 법 조문 인용 블록.
 * 본문 설명과 법령 원문을 시각적으로 구분해, 어디까지가 법에 적힌 내용인지 드러낸다.
 */
const LawQuote = styled.blockquote`
  margin: 0 0 20px;
  padding: 25px 30px;
  background: #f8f9fa;
  border-left: 4px solid #0056b3;
  border-radius: 0 8px 8px 0;

  p {
    font-size: 1rem;
    color: #333;
    line-height: 1.9;
    margin: 0;
  }

  cite {
    display: block;
    margin-top: 12px;
    font-size: 0.85rem;
    font-style: normal;
    color: #888;
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.9;
  margin-bottom: 50px;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 50px;
`;

const ServiceCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 30px 25px;
  background: #fff;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 86, 179, 0.1);
    border-color: #0056b3;
  }

  .icon { font-size: 2rem; margin-bottom: 12px; }
  h4 { font-size: 1.1rem; color: #222; margin-bottom: 8px; }
  p { font-size: 0.9rem; color: #666; line-height: 1.7; }
`;

const FeatureBox = styled.div`
  background: #f8f9fa;
  border-left: 4px solid #0056b3;
  padding: 20px 25px;
  border-radius: 0 8px 8px 0;
  margin-bottom: 15px;

  h4 { font-size: 1rem; color: #333; margin-bottom: 6px; }
  p { font-size: 0.9rem; color: #666; line-height: 1.6; }
`;

/* 인증 마크는 글이 아니라 표식이므로 본문 위에 가로로 나란히 놓는다 */
const MarkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
  flex-wrap: wrap;
  padding: 25px 30px;
  margin-bottom: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;

  img {
    height: 70px;
    width: auto;
    object-fit: contain;
  }

  .text {
    flex: 1;
    min-width: 200px;
    font-size: 0.95rem;
    color: #555;
    line-height: 1.8;
    strong { color: #1a2a40; }
  }

  @media (max-width: 768px) {
    padding: 20px;
    gap: 18px;
    img { height: 54px; }
  }
`;

const WomenEnterprise = () => {
  return (
    <SubLayout
      title="여성기업"
      sidebarMenus={promotionMenus}
      category="홍보센터"
      categoryEn="PR Center"
      bannerBg="/images/bg_building.jpg"
    >
      <MarkRow>
        <img src="/images/logo_womanenterprise.png" alt="여성기업 확인 마크" />
        <div className="text">
          주식회사 두성시스템은 <strong>2011년 12월 여성기업 확인</strong>을 받았습니다.
        </div>
      </MarkRow>

      <Intro>
        여성기업은 여성이 실질적으로 소유하고 경영하는 기업을 말합니다.
        건설업은 전통적으로 남성 중심으로 여겨져 온 분야이지만, 두성시스템은 2008년 설립 이래
        여성 경영인이 전문건설업과 건물종합관리업을 직접 이끌어 왔습니다.
      </Intro>

      <SectionLabel>정의</SectionLabel>
      <LawQuote>
        <p>
          &ldquo;여성기업&rdquo;이란 여성이 소유하고 경영하는 기업으로서 대통령령으로 정하는 기준에 해당하는
          기업을 말한다.
        </p>
        <cite>— 여성기업지원에 관한 법률 제2조 제1호</cite>
      </LawQuote>
      <Description>
        여기서 말하는 &ldquo;소유&rdquo;와 &ldquo;경영&rdquo;은 이름만 올려두는 것을 뜻하지 않습니다.
        여성이 최다출자자로서 지분을 보유하는 동시에, 대표권을 가지고 실제로 회사를 운영해야 합니다.
        두 조건 중 하나라도 빠지면 여성기업으로 인정되지 않습니다.
      </Description>

      <SectionLabel>목적</SectionLabel>
      <LawQuote>
        <p>
          이 법은 여성기업의 활동과 여성의 창업을 적극적으로 지원하여 경제영역에서 남녀의 실질적인 평등을 도모하고
          여성의 경제활동과 여성경제인의 지위 향상을 도모함으로써 국민경제 발전에 이바지함을 목적으로 한다.
        </p>
        <cite>— 여성기업지원에 관한 법률 제1조</cite>
      </LawQuote>
      <Description>
        형식적인 기회 균등을 넘어 <strong>실질적인 평등</strong>을 목표로 한다는 점이 핵심입니다.
        여성 경영인이 상대적으로 진입하기 어려웠던 분야에서도 공정하게 경쟁할 수 있도록
        공공구매와 자금·교육 지원을 제도적으로 뒷받침합니다.
      </Description>

      <SectionLabel>확인 기준</SectionLabel>
      <ServiceGrid>
        <ServiceCard>
          <div className="icon">🏢</div>
          <h4>법인 기업</h4>
          <p>여성이 대표권 있는 임원이면서 동시에 최다출자자여야 합니다.</p>
        </ServiceCard>
        <ServiceCard>
          <div className="icon">📋</div>
          <h4>개인 사업자</h4>
          <p>여성 명의로 사업자등록이 되어 있어야 합니다.</p>
        </ServiceCard>
        <ServiceCard>
          <div className="icon">🧩</div>
          <h4>협동조합</h4>
          <p>조합원 과반수가 여성이고 이사장이 여성이며, 출자금 과반수를 여성이 보유해야 합니다.</p>
        </ServiceCard>
        <ServiceCard>
          <div className="icon">🗓️</div>
          <h4>확인서 유효기간</h4>
          <p>확인일로부터 3년입니다. 대표자 변경 등으로 요건을 잃으면 그 즉시 효력이 사라집니다.</p>
        </ServiceCard>
      </ServiceGrid>

      <SectionLabel>주요 지원 제도</SectionLabel>
      <FeatureBox>
        <h4>공공기관 우선구매</h4>
        <p>공공기관은 물품·용역 구매액의 5% 이상, 공사 계약액의 3% 이상을 여성기업 제품으로 구매하도록 정해져 있습니다.</p>
      </FeatureBox>
      <FeatureBox>
        <h4>입찰 가산점</h4>
        <p>공공 경쟁입찰에서 여성기업에 가산점이 부여되어, 조달 시장 참여 기회가 넓어집니다.</p>
      </FeatureBox>
      <FeatureBox>
        <h4>자금 및 보증 지원</h4>
        <p>여성기업 전용 정책자금과 신용보증, 투자 펀드를 통해 사업 확장에 필요한 자금을 지원받을 수 있습니다.</p>
      </FeatureBox>
      <FeatureBox>
        <h4>경영 역량 강화</h4>
        <p>여성 CEO 아카데미와 경영연수, 컨설팅 등 교육 프로그램을 지원합니다.</p>
      </FeatureBox>
    </SubLayout>
  );
};

export default WomenEnterprise;
